// A one-shot "seed" for the assistant: another surface (e.g. the Meals hub's
// "Ask about dinner" button) drops a starter prompt here and navigates to the
// Assistant tab, which picks it up once, starts a fresh conversation for it,
// and either auto-sends it or pre-fills the composer. Kept in sessionStorage so
// it survives the tab switch (which remounts AssistantView) without threading
// state through the whole household context.

// A pinned object the seeded chat is about (#601). Carried alongside the seed
// prompt so AssistantView can create the fresh chat with a server-persisted
// subject, which the orchestrator re-injects every turn. Optional: the Meals
// "Ask about dinner" seed has no subject.
export type AssistantSeedSubject = {
  entityType: string;
  entityId: string | number;
  label: string;
};

export type AssistantSeed = {
  prompt: string;
  // Send immediately on arrival (when the assistant is ready), vs. only
  // pre-filling the composer for the user to review and send.
  autoSend: boolean;
  subject?: AssistantSeedSubject;
};

function seedStorageKey(householdId: string): string {
  return `assistant.seed.${householdId}`;
}

// Validate a persisted subject back into a well-formed AssistantSeedSubject, or
// drop it. A malformed subject never blocks the seed prompt from carrying.
function readSeedSubject(value: unknown): AssistantSeedSubject | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<AssistantSeedSubject>;
  const entityType = typeof candidate.entityType === "string" ? candidate.entityType.trim() : "";
  const label = typeof candidate.label === "string" ? candidate.label.trim() : "";
  const entityId = candidate.entityId;
  const hasId = (typeof entityId === "string" && entityId.trim()) || (typeof entityId === "number" && Number.isFinite(entityId));
  if (!entityType || !label || !hasId) return null;
  return { entityType, entityId: entityId as string | number, label };
}

export function writeAssistantSeed(householdId: string, seed: AssistantSeed): void {
  try {
    window.sessionStorage.setItem(seedStorageKey(householdId), JSON.stringify(seed));
  } catch {
    // Storage unavailable (private mode / quota) — the seed just won't carry.
  }
}

/** Reads the pending seed without consuming it. */
export function readAssistantSeed(householdId: string): AssistantSeed | null {
  try {
    const raw = window.sessionStorage.getItem(seedStorageKey(householdId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AssistantSeed>;
    if (!parsed || typeof parsed.prompt !== "string" || !parsed.prompt.trim()) return null;
    const subject = readSeedSubject(parsed.subject);
    return { prompt: parsed.prompt, autoSend: parsed.autoSend !== false, ...(subject ? { subject } : {}) };
  } catch {
    return null;
  }
}

export function clearAssistantSeed(householdId: string): void {
  try {
    window.sessionStorage.removeItem(seedStorageKey(householdId));
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
